// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
//  Author    : Tobias Schwarzkopf, Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 159808 $
//  Purpose   : parts for tests
// =============================================================================
// ---Third-party imports
import React, {Component, Fragment} from 'react'
import {Header, Divider, Label, Tab, Image, Icon, List, Input, Container} from 'semantic-ui-react'
import KeyHandler, {KEYPRESS, KEYDOWN} from 'react-key-handler'

// ---Framework imports
import {Actions, Sel} from 'uix/registries'
import {Parts} from 'uix/registry'
import {loadLazy as ll, Part, funcPart, StandalonePart} from 'uix/parts'
import log from 'uix/logging'
import {keys, dict, iterItems, mapItems, values, sum, flatten, sorted, zip} from 'ldash'
import {ucFirst, prettyBytes} from 'uix/utils'
import {Icons, vicon} from 'uix/graphics'
import {Editor, Differ, HeaderSegment, Link, XMenu} from 'uix/components'
import {
  usePersistent,
  useSelector,
  useActions,
  useApi,
  useState,
  useDelay,
  useEffectSSR as useEffect,
} from 'uix/hooks'
import {TreeNodeItem} from 'uix/db/components'

import {Plot} from 'uix/modules/stats/plots'

export * from './persistUi'

// ---Globals

export const TestExplorer = ll(() => import(/* webpackChunkName: 'tests' */ './explorer'))

export class TestDummy extends Part {
  _render() {
    let {go, wiki, partcfg, request} = this.props
    if (!wiki) {
      return
    }
    let run = () => {
      // removal is checked in testProdBuild()
      log.info('*** removed by babel plugin remove-logapi-calls ***')
      log.error('*** removed by babel plugin remove-logapi-calls ***')
      log.exception('*** NOT removed by babel plugin remove-logapi-calls ***')

      go({tabname: partcfg.clickTarget})
    }
    return (
      <>
        <span>
          This is Tab <b>{request.tabname}</b> with Wiki:{' '}
        </span>
        <b>{wiki.identifier}</b>
        <div onClick={run}>Click me.</div>
      </>
    )
  }
}

let Umbrella = () => (
  <svg
    className="umbrella"
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    viewBox="0 0 32 32"
    aria-labelledby="title"
  >
    <title id="title">Umbrella Icon</title>
    <path d="M27 14h5c0-1.105-1.119-2-2.5-2s-2.5 0.895-2.5 2v0zM27 14c0-1.105-1.119-2-2.5-2s-2.5 0.895-2.5 2c0-1.105-1.119-2-2.5-2s-2.5 0.895-2.5 2v0 14c0 1.112-0.895 2-2 2-1.112 0-2-0.896-2-2.001v-1.494c0-0.291 0.224-0.505 0.5-0.505 0.268 0 0.5 0.226 0.5 0.505v1.505c0 0.547 0.444 0.991 1 0.991 0.552 0 1-0.451 1-0.991v-14.009c0-1.105-1.119-2-2.5-2s-2.5 0.895-2.5 2c0-1.105-1.119-2-2.5-2s-2.5 0.895-2.5 2c0-1.105-1.119-2-2.5-2s-2.5 0.895-2.5 2c0-5.415 6.671-9.825 15-9.995v-1.506c0-0.283 0.224-0.499 0.5-0.499 0.268 0 0.5 0.224 0.5 0.499v1.506c8.329 0.17 15 4.58 15 9.995h-5z" />
  </svg>
)

export class TestGraphics extends Part {
  static uiDefaults = {
    activeIndex: 0,
    __version__: 1,
  }

  static getGraphics = [
    'types',
    'states',
    'controls',
    'license',
    'connectors',
    'common',
    'tree',
    'icons',
  ]

  _render() {
    let {ui, setUI, Graphics, go, request} = this.props
    let {types, states, controls, license, connectors, common, tree, icons} = Graphics

    let path2grouped = imported =>
      dict(
        keys(imported)
          .filter(k => k.split('/').length == 2)
          .map(k => [k, k.split('/')])
      )

    let groupGraphics = imported => {
      let groupedImported = {}
      iterItems(path2grouped(imported), (k, [datatype, subtype]) => {
        if (!groupedImported[datatype]) {
          groupedImported[datatype] = {}
        }
        groupedImported[datatype][subtype] = k
      })
      return groupedImported
    }

    let groupedTypes = groupGraphics(types)
    let groupedStates = groupGraphics(states)

    const _ = imported => dict(keys(imported).map(k => [k, k]))
    let groupedOther = {
      controls: _(controls),
      common: _(common),
      tree: _(tree),
      icons: _(icons),
      license: _(license),
      connectors: _(connectors),
    }
    let other = {...controls, ...common, ...tree, ...icons, ...license, ...connectors}

    //~ this.pprint({0: groupedTypes, 1: groupedStates, 2: groupedOther}[ui.activeIndex])
    //~ vl.pprint(controls)
    let draw = [
      (imported, subtype, name) => (
        <Label basic key={name}>
          <Image inline centered src={imported[name]} />
          {' ' + name.split('/').slice(-1)[0]}
        </Label>
      ),
      (imported, subtype, name) => (
        <img
          width="32rem"
          key={name}
          title={subtype}
          src={imported[name]}
          style={{verticalAlign: 'middle'}}
        />
      ),
      (imported, subtype, name) => (
        <img
          width="128rem"
          key={name}
          title={subtype}
          src={imported[name]}
          style={{border: '1px solid black'}}
        />
      ),
    ]
    let drawMode = request.query.draw || 0
    let drawFunc = draw[drawMode]

    let sections = (groupedImported, imported) =>
      mapItems(groupedImported, (datatype, subtypes) => {
        const sts = mapItems(subtypes, (subtype, name) => drawFunc(imported, subtype, name))
        let svgsize = sum(values(subtypes).map(name => imported[name].length))

        return (
          <div key={datatype}>
            <Header
              as="h3"
              style={{cursor: 'pointer'}}
              image={imported[values(subtypes)[0]]}
              content={ucFirst(datatype)}
              onClick={() => go({query: {draw: (drawMode + 1) % draw.length}})}
            />
            {sts}
            <Label content={`Size: ${prettyBytes(svgsize)}`} size="mini" pointing="left" />
            <Divider />
          </div>
        )
      })

    let renderFonts = () => (
      <>
        <Label basic icon="clock outline" content="sui-clock" />
        <Label basic icon={vicon('clock_fa')} content="vl-clock" />
        <Label basic icon="warehouse" content="sui-warehouse" />
        {flatten([
          ...keys(Icons).map(name => (
            <div key={name}>
              <Label basic icon={Icons[name]} content={name} />
              <Label basic icon={vicon(name, {loading: true})} content={name} />
            </div>
          )),
        ])}
      </>
    )

    let pane = content => (
      <Tab.Pane
        style={{
          flex: 1,
          overflow: 'auto',
        }}
        attached={false}
      >
        {content}
      </Tab.Pane>
    )

    let panes = [
      {menuItem: 'Types', render: () => pane(sections(groupedTypes, types))},
      {menuItem: 'States', render: () => pane(sections(groupedStates, states))},
      {menuItem: 'Other', render: () => pane(sections(groupedOther, other))},
      {menuItem: 'Fonts', render: () => pane(renderFonts())},
    ]

    return (
      <>
        <Tab
          style={{display: 'flex', flexDirection: 'column', flex: 1}}
          menu={{secondary: true, pointing: true}}
          panes={panes}
          activeIndex={ui.activeIndex}
          onTabChange={(e, data) => setUI({activeIndex: data.activeIndex})}
        />
      </>
    )
  }
}

export const TestEditor = funcPart({partStyle: {display: 'flex', flex: 1}}, ({}) => {
  let [text, setText] = usePersistent('TestText', '')
  return (
    <HeaderSegment header="Editor" flex>
      <Editor
        flex
        text={text}
        opts={{mode: 'markdown'}}
        onBeforeChange={({value}) => setText(value)}
      />
    </HeaderSegment>
  )
})

export const TestDiffer = funcPart({partStyle: {display: 'flex', flex: 1}}, ({}) => {
  //~ let [text, setText] = usePersistent('TestText', '')
  return (
    <HeaderSegment header="Differ" flex portal="DifferPortal">
      <Differ
        flex
        textLeft={'left'}
        textMiddle={'right'}
        headLeft="left file"
        headMiddle="right file"
        mode={'markdown'}
        xml={false}
        menuPortal="DifferPortal"
      />
    </HeaderSegment>
  )
})

export const ListTestPages = ({}) => {
  let tcfg = useSelector(Sel.domainClassCfgs)
  let go = useActions(Actions.go)
  let [searchString, setSearchString] = useState('')
  let [focused, setFocused] = useState(null)

  let tabs = sorted(keys(tcfg.test.tabs)).filter(e => e.includes(searchString))

  let onKey = event => {
    if (!focused || tabs.indexOf(focused) == -1) {
      setFocused(tabs[0])
    } else {
      let idx = tabs.indexOf(focused)
      if (event.key == 'ArrowUp') {
        setFocused(tabs[Math.max(idx - 1, 0)])
      } else if (event.key == 'ArrowDown') {
        setFocused(tabs[Math.min(idx + 1, tabs.length - 1)])
      } else if (event.key == 'Enter') {
        go({tabname: focused})
      }
    }
  }

  let links = tabs.map(name => (
    <List.Item key={name} active={focused == name}>
      <Link tabname={name}>{name}</Link>
    </List.Item>
  ))

  let keycodes = ['ArrowUp', 'ArrowDown']
  let keyhandlers = keycodes.map(k => (
    <KeyHandler key={k} keyEventName={KEYDOWN} code={k} onKeyHandle={onKey} />
  ))

  return (
    <Container>
      {keyhandlers}
      <Input
        size="small"
        value={searchString}
        onChange={(event, data) => setSearchString(data.value)}
        ref={ref => ref && ref.focus()}
        onKeyDown={onKey}
      />
      <List link size="medium">
        {links}
      </List>
    </Container>
  )
}

export const TestParts = funcPart(
  {getGraphics: ['types'], partStyle: {display: 'flex', flex: 1}},
  ({go, request}) => {
    let [filter, setFilter] = usePersistent('partSearchFilter', '')
    let parts = Parts.components
    let filtered = keys(parts).filter(n => n.toLowerCase().includes(filter.toLowerCase()))
    let ptype = request.ctxid || request.id
    let otid = request.ctxid ? request.id : null

    let parttype = parts[ptype] ? ptype : null
    let items = sorted(filtered)
      .slice(0, 7)
      .map(n => (
        <XMenu.Item
          key={n}
          content={n}
          active={ptype == n}
          onClick={() => go(request.ctxid ? {ctxid: n} : {id: n})}
        />
      ))
    //~ vl.pprint(request)
    let menu = (
      <XMenu>
        {items}
        <XMenu.FilterInput defaultValue={filter} onValueChange={setFilter} />
        <XMenu.Input
          label="ID"
          value={request.ctxid && request.id ? request.id : ''}
          onValueChange={v => go({ctxid: ptype, id: v})}
        />
      </XMenu>
    )
    return (
      <HeaderSegment header={parttype} subheader={<TreeNodeItem otid={otid} />} menu={menu} flex>
        {parttype && (
          <StandalonePart
            partCfg={{style: {display: 'flex', flex: 1, overflow: 'auto'}}}
            type={parttype}
            name={`Test_${parttype}`}
          />
        )}
      </HeaderSegment>
    )
  }
)

export const TestUseApi = ({}) => {
  let res = useApi('info')
  return JSON.stringify(res || 'null')
}

export const TestPlots = funcPart({partStyle: {display: 'flex', flex: 1}}, () => {
  let [plotName, setPlotName] = useState('zipcapsule')
  let menu = (
    <XMenu>
      <XMenu.Dropdown
        options={keys(Plot.PLOTS).map(v => ({
          value: v,
          key: v,
          text: v,
        }))}
        value={plotName}
        onSetValue={setPlotName}
      />
    </XMenu>
  )
  return (
    <HeaderSegment flex header="Plots" menu={menu} portal="plotMenuPortal">
      <Plot flex name={plotName} menuPortal="plotMenuPortal" showMenu showLegend />
    </HeaderSegment>
  )
})

export const TestRenderWaterfall = ({}) => {
  /* simulate cascading changes on store via effects triggered by rendering
  (test SSR/waitForIdle )
  */
  let setDebugData = useActions('setDebugData')
  let waterfall = useSelector(root => root.debug.waterfall) || 0
  useEffect(() => {
    log.debug('set waterfall', waterfall + 1)
    setDebugData({waterfall: waterfall + 1})
  }, [waterfall < 5 ? waterfall : 5])
  return 'waterfall: ' + waterfall
}
